import { Timestamp } from 'firebase/firestore'
import { ServerClock, emptyRegime, newRacePlan } from '@racedash/shared'
import type { RacePlanDoc } from '@racedash/shared'
import type { ScreenContext } from './screenContext'
import type {
  CarStanding,
  FcyPeriodDoc,
  FuelDoc,
  LapDoc,
  LiveMessagesDoc,
  MessageDoc,
  Stint,
  StintsDoc,
} from '@racedash/shared'
import type { DashboardData } from './useSessionData'

/**
 * Plausible sessions, for arranging a dashboard when nothing is capturing.
 *
 * One coherent fabricated session rather than a sample value per widget. That
 * matters: car 436 is fourth in the standings, fourth in the lap list, and its
 * best lap is the one the lap-time tile shows. Independent per-widget samples
 * would drift apart and you would be arranging a dashboard that cannot exist.
 *
 * Modelled on a Fun Cup enduro at Le Castellet: one class, a large field, lap
 * times around two minutes.
 *
 * Three of them, because a dashboard spends real time in each and they look
 * very different. Judging a layout only against a healthy mid-race session is
 * how you end up with one that falls apart in the first ten minutes — when
 * nobody has set a lap yet and half the tiles have nothing to say.
 */
export type SampleScenario = 'midRace' | 'opening' | 'stoppage'

export const sampleScenarios: { id: SampleScenario; label: string; description: string }[] = [
  { id: 'midRace', label: 'Mid-race', description: 'Three hours in, everything populated.' },
  { id: 'opening', label: 'First laps', description: 'Nothing has happened yet — most tiles are empty.' },
  { id: 'stoppage', label: 'Red flag', description: 'The clock is stopped and the track is red.' },
]

export const sampleCar = '436'

const at = (offsetMs: number) => Timestamp.fromDate(new Date(Date.now() + offsetMs))

const lapTimes = [
  126_412, 125_980, 126_733, 141_205, 138_640, 126_104, 125_711, 126_902, 127_355, 126_048,
]

function completedLaps(): LapDoc[] {
  return lapTimes.map((ms, i) => {
    // Two laps in the middle were run under a full course yellow, which is what
    // the flagged sectors and the slower times are.
    const neutralised = ms > 135_000
    const s1 = Math.round(ms * 0.31)
    const s2 = Math.round(ms * 0.36)
    return {
      car: sampleCar,
      lap: 84 - i,
      completedAt: at(-i * 126_000),
      lapTimeMs: ms,
      s1Ms: s1,
      s2Ms: s2,
      s3Ms: ms - s1 - s2,
      s1Fcy: neutralised,
      s2Fcy: neutralised,
      s3Fcy: neutralised,
      position: 4,
      driver: 'A. Marsh',
      class: 'Fun Cup',
      gapToLeaderMs: 44_120 + i * 900,
      gapToLeaderLaps: 0,
    }
  })
}

/**
 * The lap in progress: two sectors in, no lap time yet.
 *
 * This is what the interpreter writes as each sector lands, and what the sector
 * widget exists to show.
 */
const runningLap: LapDoc = {
  car: sampleCar,
  lap: 85,
  s1Ms: 39_180,
  s2Ms: 45_602,
  position: 4,
  driver: 'A. Marsh',
  class: 'Fun Cup',
}

function field(): CarStanding[] {
  // Sectors, gaps, drivers and states across the whole field, not just the
  // watched car. The sample was built for one-car widgets, and a timing table
  // over it showed a column of dashes — flattering a screen that could not
  // actually be judged.
  const entries: [string, string, string, number, number | null][] = [
    ['211', 'Écurie Vitesse', 'P. Vermeulen', 124_889, null],
    ['58', 'Northgate Racing', 'R. Hall', 125_101, 1_204],
    ['7', 'Carrick Motorsport', 'M. Anand', 125_402, 12_880],
    [sampleCar, 'Quarendon Racing', 'A. Marsh', 125_711, 44_120],
    ['07', 'Lamplight Racing', 'D. Whitcombe', 126_004, 61_940],
    ['19', 'Bruntingthorpe AMR', 'K. Sorensen', 126_781, 92_310],
  ]

  const cars: CarStanding[] = entries.map(([number, team, driver, best, gap], i) => ({
    number,
    position: i + 1,
    team,
    driver,
    class: 'Fun Cup',
    classPosition: i + 1,
    lap: i < 2 ? 86 : 85,
    // Canonical states, as the interpreter writes them: RUN / IN / OUT / FUEL.
    // Anything else reads as unknown, which is exactly what a wrong sample
    // looked like here.
    state: number === '19' ? 'IN' : 'RUN',
    lastLapMs: best + 2_400 + i * 300,
    bestLapMs: best,
    s1Ms: 39_000 + i * 180,
    s2Ms: 45_300 + i * 220,
    s3Ms: 41_400 + i * 160,
    gapToLeaderMs: gap,
    gapToLeaderLaps: 0,
    intervalAheadMs: i === 0 ? null : 1_200 + i * 900,
    intervalAheadLaps: 0,
    stops: 3,
  }))

  return [
    ...cars,
    // Out of the garage but yet to complete a lap: no times at all.
    {
      number: '92',
      position: 7,
      team: 'Silverline',
      driver: 'T. Beaumont',
      class: 'Fun Cup',
      classPosition: 7,
      lap: 0,
      state: 'OUT',
      stops: 0,
    },
    // Retired: no position, so it holds nobody up in the class ranking and it
    // sorts to the bottom rather than to the top.
    {
      number: '3',
      position: 0,
      team: 'Halstead Competition',
      driver: 'M. Lefevre',
      class: 'Fun Cup',
      lap: 41,
      bestLapMs: 126_990,
      state: 'RUN',
      stops: 2,
    },
  ]
}

const raceControl: MessageDoc[] = [
  { message: 'FULL COURSE YELLOW ENDED', elapsedMs: 10_620_000, receivedAt: at(-120_000) },
  { message: 'CAR 3 STOPPED AT TURN 9 — FULL COURSE YELLOW', elapsedMs: 10_140_000, receivedAt: at(-600_000) },
  { message: 'CAR 92 — TRACK LIMITS, TURN 11, WARNING 2', elapsedMs: 9_480_000, receivedAt: at(-1_260_000) },
  { message: 'PIT LANE OPEN', elapsedMs: 8_940_000, receivedAt: at(-1_800_000) },
  { message: 'CAR 58 — 10 SECOND TIME PENALTY, PIT LANE SPEEDING', elapsedMs: 7_200_000, receivedAt: at(-3_540_000) },
  { message: 'RACE START', elapsedMs: 0, receivedAt: at(-10_800_000) },
]

/**
 * Stints for a handful of cars, so the chart can be judged without live timing.
 *
 * Deliberately not synchronised: the interesting thing a stint chart shows is
 * cars *out of step* with each other, and a set of neatly aligned rows would
 * flatter a layout that hides exactly that.
 */
function sampleStints(endedAt: Date): StintsDoc[] {
  const start = endedAt.getTime() - 3 * 3_600_000
  const cars: { car: string; boundaries: number[]; driver: string[] }[] = [
    { car: sampleCar, boundaries: [0, 55, 118, 180], driver: ['A. Marsh', 'J. Reid', 'A. Marsh'] },
    { car: '211', boundaries: [0, 62, 125, 180], driver: ['P. Vermeulen', 'K. Dumont', 'P. Vermeulen'] },
    { car: '58', boundaries: [0, 48, 96, 148, 180], driver: ['R. Hall', 'R. Hall', 'S. Okafor', 'S. Okafor'] },
    // Retired an hour in: its last stint is closed, and it has no open one.
    { car: '3', boundaries: [0, 41], driver: ['M. Lefèvre'] },
  ]

  return cars.map(({ car, boundaries, driver }) => {
    const stints: Stint[] = []
    for (let i = 0; i < boundaries.length - 1; i++) {
      const from = start + (boundaries[i] ?? 0) * 60_000
      const to = start + (boundaries[i + 1] ?? 0) * 60_000
      const laps = Math.max(1, Math.round((to - from) / 126_000))
      const open = i === boundaries.length - 2 && car !== '3'
      const base = 125_500 + i * 400
      stints.push({
        number: i,
        firstLap: 1 + i * 25,
        lastLap: i * 25 + laps,
        startedAt: Timestamp.fromDate(new Date(from)),
        endedAt: open ? null : Timestamp.fromDate(new Date(to)),
        open,
        laps,
        totalMs: laps * (base + 2_500),
        greenLaps: Math.max(1, laps - 2),
        greenTotalMs: Math.max(1, laps - 2) * base,
        bestLapMs: base - 900,
        // The second stint of the primary car carries two names, which is what
        // a driver change reported late actually looks like.
        driverLaps:
          car === sampleCar && i === 1
            ? { 'A. Marsh': 2, 'J. Reid': Math.max(1, laps - 2) }
            : { [driver[i] ?? 'Unknown']: laps },
      })
    }
    return { car, updatedAt: Timestamp.fromDate(endedAt), stints }
  })
}

const sampleFcy = (endedAt: Date): FcyPeriodDoc[] => {
  const start = endedAt.getTime() - 3 * 3_600_000
  return [
    {
      startedAt: Timestamp.fromDate(new Date(start + 68 * 60_000)),
      endedAt: Timestamp.fromDate(new Date(start + 79 * 60_000)),
      state: 'FCY',
    },
    {
      startedAt: Timestamp.fromDate(new Date(start + 142 * 60_000)),
      endedAt: Timestamp.fromDate(new Date(start + 147 * 60_000)),
      state: 'SC',
    },
  ]
}

/**
 * What the board is showing, which is not the top of the log.
 *
 * Race control displays a message or two and then clears them; everything else
 * in the log has already gone. Slicing the log looked plausible and taught the
 * wrong thing — it made the board read as "the last three things ever said",
 * which is exactly the confusion the two-collection split exists to avoid.
 *
 * Each entry keeps the session time it appeared, because that is a fact worth
 * showing: this went up at 2:57 and is still up.
 */
const board = (items: { text: string; elapsedMs: number }[]): LiveMessagesDoc => ({
  updatedAt: at(0),
  messages: items.map((m, i) => ({
    id: `b${i}`,
    at: `14:0${i}`,
    message: m.text,
    elapsedMs: m.elapsedMs,
  })),
})

/** Sample data, shaped exactly as a live listener would deliver it. */
/**
 * Three fills for the primary car and one for a rival: two at stops, the
 * second with the laps the first lasted, and the one still in the car.
 */
function sampleFuel(now: Date): FuelDoc[] {
  const ago = (min: number) => Timestamp.fromDate(new Date(now.getTime() - min * 60_000))
  return [
    { car: sampleCar, at: ago(178), litres: 46, source: 'kronos', kind: 'before', lap: 0, lapsOnFuel: 23 },
    {
      car: sampleCar, at: ago(129), litres: 41.2, source: 'kronos', kind: 'stop', stopNumber: 1, lap: 23,
      lapsSincePrevious: 23, litresPerLap: 41.2 / 23, lapsOnFuel: 24,
    },
    {
      car: sampleCar, at: ago(78), litres: 43.6, source: 'kronos', kind: 'stop', stopNumber: 2, lap: 47,
      lapsSincePrevious: 24, litresPerLap: 43.6 / 24, lapsOnFuel: 37, open: true,
    },
    {
      car: '58', at: ago(101), litres: 44.9, source: 'kronos', kind: 'stop', stopNumber: 1, lap: 36,
      lapsSincePrevious: 36, sinceStart: true, litresPerLap: 44.9 / 36, lapsOnFuel: 46, open: true,
    },
  ]
}

export function sampleData(scenario: SampleScenario = 'midRace'): DashboardData {
  const now = new Date()
  const base = {
    // Sample data is not a bucket. Saying so is what stops a chart that opens a
    // listener of its own from trying to read one that does not exist.
    sessionId: null,
    // No stops in the samples. A planner widget then reads as "nothing has
    // happened yet", which is honest for a layout being arranged.
    pitStops: [],
    // No samples taken, so this reads as "the viewer's clock is right", which is
    // the correct assumption when there is no capture job to compare with.
    clock: new ServerClock(),
    // A sample is always "live": it exists to show a layout working, and a
    // frozen one would draw every elapsed figure as a dash.
    asAt: (browserNow: Date) => browserNow,
    errors: [],
    stints: sampleStints(now),
    fcyPeriods: sampleFcy(now),
    fuel: sampleFuel(now),
    // Sector bests a little under the sample's running figures, so the tile
    // that shows both is showing a comparison rather than two copies of one
    // number. The ideal they add to is inside the sample's best lap.
    carSummary: {
      car: sampleCar,
      bestLapMs: 126_990,
      bestS1Ms: 38_820,
      bestS2Ms: 44_260,
      bestS3Ms: 43_640,
    } as DashboardData['carSummary'],
  }

  if (scenario === 'opening') {
    // Four minutes in, first lap not yet complete. Almost nothing is known,
    // which is exactly the state a dashboard is in when it is first looked at.
    const cars = field().map((c) => ({
      ...c,
      lap: 1,
      lastLapMs: null,
      bestLapMs: null,
      gapToLeaderMs: null,
      intervalAheadMs: null,
      s1Ms: null,
      s2Ms: null,
      s3Ms: null,
      stops: 0,
      state: 'RUN',
    }))
    return {
      ...base,
      session: {
        trackId: 'paul-ricard',
        seriesNameRaw: 'Fun Cup',
        sessionNameRaw: 'Course 1',
        status: 'RUNNING',
        trackState: 'GREEN',
        clock: { at: at(0), elapsedMs: 240_000, remainingMs: 14_160_000, running: true },
        lastSeenAt: at(0),
      },
      standings: { cars, updatedAt: at(0) },
      // One sector in on the opening lap, and nothing behind it.
      laps: [{ car: sampleCar, lap: 1, s1Ms: 41_902, position: 4, driver: 'A. Marsh', class: 'Fun Cup' }],
      messages: [{ message: 'RACE START', elapsedMs: 0, receivedAt: at(-240_000) }],
      // Four minutes in, and the only thing still on the board is the start.
      liveMessages: board([{ text: 'RACE START', elapsedMs: 0 }]),
      // Nothing has happened yet, so no stints and no neutralisations. This is
      // the state a chart is in for the first ten minutes, and it should look
      // deliberate rather than broken.
      stints: [],
      fcyPeriods: [],
    }
  }

  if (scenario === 'stoppage') {
    // The case the clock checkpoints exist for. Everything is populated and
    // nothing is moving, so a countdown that keeps counting is visibly wrong.
    return {
      ...base,
      session: {
        trackId: 'paul-ricard',
        seriesNameRaw: 'Fun Cup',
        sessionNameRaw: 'Course 1',
        status: 'RUNNING',
        trackState: 'RED',
        clock: { at: at(-420_000), elapsedMs: 10_800_000, remainingMs: 3_600_000, running: false },
        lastSeenAt: at(0),
      },
      standings: { cars: field(), updatedAt: at(-420_000) },
      laps: completedLaps(),
      messages: [
        { message: 'PIT LANE CLOSED', elapsedMs: 10_800_000, receivedAt: at(-420_000) },
        { message: 'RED FLAG — ALL CARS TO THE PIT LANE', elapsedMs: 10_800_000, receivedAt: at(-420_000) },
        ...raceControl,
      ],
      liveMessages: board([
        { text: 'RED FLAG — ALL CARS TO THE PIT LANE', elapsedMs: 10_800_000 },
        { text: 'PIT LANE CLOSED', elapsedMs: 10_800_000 },
      ]),
      // Both board entries appear here too: a message on the board is one that
      // appeared, and the log is the record that it did.
    }
  }

  return {
    ...base,
    session: {
      trackId: 'paul-ricard',
      seriesNameRaw: 'Fun Cup',
      sessionNameRaw: 'Course 1',
      status: 'RUNNING',
      trackState: 'GREEN',
      // Three hours in, one to go, and running — so the countdown ticks.
      clock: { at: at(0), elapsedMs: 10_800_000, remainingMs: 3_600_000, running: true },
      lastSeenAt: at(0),
    },
    standings: { cars: field(), updatedAt: at(0) },
    laps: [runningLap, ...completedLaps()],
    messages: raceControl,
    // Two messages up, both current. Everything else in the log above has
    // already been cleared from the board — which is the point of the pair.
    liveMessages: board([
      { text: 'FULL COURSE YELLOW ENDED', elapsedMs: 10_620_000 },
      { text: 'CAR 92 — TRACK LIMITS, TURN 11, WARNING 2', elapsedMs: 9_480_000 },
    ]),
  }
}

/**
 * A team to go with the sample race, so the tiles that are about the team can
 * be judged too.
 *
 * The preview handed every screen noContext — no drivers, no plan, no
 * declarations — so the driver, stop-by, budget and planner tiles rendered
 * their empty states and nothing else. A page that exists to judge layouts
 * could not show a third of them, which is how a truncated driver name went
 * unreproduced: the tile never had a "Next up" line to be truncated against.
 *
 * Long names on purpose. That is the case a layout has to survive.
 */
export function sampleContext(now: Date): ScreenContext {
  const start = new Date(now.getTime() - 3 * 3_600_000)
  const end = new Date(start.getTime() + 4 * 3_600_000)
  const drivers = [
    { id: 'd-marsh', name: 'Alexandra Marsh-Whitfield' },
    { id: 'd-reid', name: 'Tom Reid' },
    { id: 'd-cowley', name: 'Benedict Cowley-Harrington' },
  ]
  const plan: RacePlanDoc = {
    ...newRacePlan('sample-event', 'Sample 4 Hours'),
    startsAt: start.getTime(),
    endsAt: end.getTime(),
    drivers: drivers.map((d) => d.id),
    regime: {
      ...emptyRegime(),
      windows: {
        anchor: 'timeOfDay',
        // Five twenty-minute windows on a forty-minute cycle, the Fun Cup shape.
        entries: Array.from({ length: 5 }, (_, i) => ({
          opensAt: start.getTime() + (30 + i * 40) * 60_000,
          closesAt: start.getTime() + (50 + i * 40) * 60_000,
        })),
      },
    },
  }
  return {
    viewId: 'preview',
    ourCars: [sampleCar],
    drivers,
    entryDrivers: { [sampleCar]: drivers.map((d) => d.id) },
    declarations: [
      { car: sampleCar, driverId: 'd-marsh', from: start },
      { car: sampleCar, driverId: 'd-reid', from: new Date(start.getTime() + 75 * 60_000) },
      { car: sampleCar, driverId: 'd-cowley', from: new Date(start.getTime() + 150 * 60_000) },
    ],
    plan,
    sessionStart: start,
    sessionEnd: end,
  }
}
